import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trophy, Users, BookOpen, Gamepad2, Lock,
  ArrowRight, ExternalLink, AlertCircle
} from 'lucide-react';
import { sharingService, ShareableContent, ShareLink } from '@/services/sharingService';
import { supabaseClient } from '@/services/supabaseClient';

export const SharePage: React.FC = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<ShareableContent | null>(null);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);

  useEffect(() => {
    if (shortCode) {
      loadShareContent();
    }
  }, [shortCode]);

  const loadShareContent = async (attemptPassword?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await sharingService.resolveShareLink(
        shortCode!,
        attemptPassword
      );
      
      setContent(result.content);
      setShareLink(result.link);
      
      // Track the view
      trackShareView(result.link.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load share';
      
      if (message.includes('password')) {
        setNeedsPassword(true);
        setError('This share is password protected');
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const trackShareView = async (shareLinkId: string) => {
    try {
      await supabaseClient.from('share_interactions').insert({
        share_link_id: shareLinkId,
        viewer_id: supabaseClient.auth.user()?.id,
        referrer: document.referrer,
        user_agent: navigator.userAgent
      });
    } catch (err) {
      console.error('[SharePage] Error tracking view:', err);
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadShareContent(password);
  };

  const handleJoinGame = async () => {
    if (!content || content.type !== 'game_invite') return;

    // Navigate to the game with the room ID
    navigate(`/game/join/${content.metadata.roomId}`);
  };

  const renderContent = () => {
    if (!content) return null;

    switch (content.type) {
      case 'progress':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <BookOpen className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">{content.title}</h1>
              <p className="text-gray-400">{content.description}</p>
            </div>

            {content.imageUrl && (
              <img
                src={content.imageUrl}
                alt="Story progress"
                className="w-full rounded-lg shadow-xl"
              />
            )}

            <div className="bg-gray-800/50 rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Archetype</p>
                  <p className="text-lg font-semibold text-cyan-300">
                    {content.metadata.archetype}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Level</p>
                  <p className="text-lg font-semibold text-yellow-400">
                    {content.metadata.level}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Chapters</p>
                  <p className="text-lg font-semibold text-purple-400">
                    {content.metadata.chaptersCompleted}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Choices</p>
                  <p className="text-lg font-semibold text-green-400">
                    {content.metadata.choicesMade}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/')}
              className="w-full py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Start Your Own Adventure
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        );

      case 'game_invite':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Gamepad2 className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">{content.title}</h1>
              <p className="text-gray-400">{content.description}</p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Host</span>
                <span className="text-white font-semibold">
                  {content.metadata.hostName}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Game Mode</span>
                <span className="text-cyan-400 font-semibold uppercase">
                  {content.metadata.gameMode}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Players</span>
                <span className="text-green-400 font-semibold">
                  {content.metadata.joinedPlayers.length} / {content.metadata.maxPlayers}
                </span>
              </div>
            </div>

            {content.metadata.joinedPlayers.length < content.metadata.maxPlayers ? (
              <button
                onClick={handleJoinGame}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                Join Game
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="text-center text-red-400 py-4">
                <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                This game is full
              </div>
            )}
          </div>
        );

      case 'character':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <img
                src={content.imageUrl || '/default-avatar.png'}
                alt="Character"
                className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-cyan-500"
              />
              <h1 className="text-3xl font-bold text-white mb-2">{content.title}</h1>
              <p className="text-gray-400">{content.description}</p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-cyan-300 mb-4">Character Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(content.metadata.stats || {}).map(([stat, value]) => (
                  <div key={stat}>
                    <p className="text-sm text-gray-400 capitalize">{stat}</p>
                    <p className="text-lg font-semibold text-white">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {content.metadata.traits && (
              <div className="bg-gray-800/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-cyan-300 mb-4">Traits</h3>
                <div className="flex flex-wrap gap-2">
                  {content.metadata.traits.map((trait: string) => (
                    <span
                      key={trait}
                      className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm"
                    >
                      {trait}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'combat_victory':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">{content.title}</h1>
              <p className="text-gray-400">{content.description}</p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold text-cyan-300 mb-2">Rewards</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Experience</p>
                  <p className="text-lg font-semibold text-green-400">
                    +{content.metadata.rewards.experience} XP
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Credits</p>
                  <p className="text-lg font-semibold text-yellow-400">
                    +{content.metadata.rewards.credits}
                  </p>
                </div>
              </div>
              
              {content.metadata.rewards.items?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-400 mb-2">Items Earned</p>
                  <div className="space-y-1">
                    {content.metadata.rewards.items.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className={`text-${item.rarity}-400`}>●</span>
                        <span className="text-gray-300">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-2">{content.title}</h1>
            <p className="text-gray-400">{content.description}</p>
            {content.imageUrl && (
              <img
                src={content.imageUrl}
                alt="Shared content"
                className="w-full rounded-lg shadow-xl mt-6"
              />
            )}
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-3 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading shared content...</p>
        </div>
      </div>
    );
  }

  if (needsPassword && !content) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 rounded-xl p-8 max-w-md w-full border border-gray-700"
        >
          <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white text-center mb-4">
            Password Protected
          </h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
              autoFocus
            />
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            <button
              type="submit"
              className="w-full py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg transition-colors"
            >
              Unlock
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (error && !needsPassword) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Share Not Found</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 rounded-xl p-8 border border-gray-700"
        >
          {renderContent()}
          
          {shareLink && (
            <div className="mt-8 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
              Shared {new Date(content!.createdAt).toLocaleDateString()} · 
              {' '}{shareLink.viewCount} views
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};